import { S3File } from 'types/File';
import { Grid, Tooltip } from '@mui/material';
import useStyles from './AdminMessageViewerAttachments.styles';
import Image from 'components/medias/Image';
import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { emptyFunction } from 'utils/functions';
import AudioPlayer from 'components/medias/AudioPlayer/AudioPlayer';
import clsx from 'clsx';
import {downloadFileFromURL, parseFileName} from 'utils/file';
import {Video} from 'components';
import {AudioIcon, ExcelIcon, PdfIcon, PowerPointIcon, UnknownIcon, WordIcon} from 'assets/icons/file_icons';

type DocumentPreviewPureProps = {
  icon: string;
  handleDownload: () => void;
};

export const DocumentPreviewPure = ({
  icon,
  handleDownload,
}: DocumentPreviewPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <Image className={classes.image} src={icon} onClick={handleDownload} />
  );
};

type ImagePreviewProps = {
  url: string;
  handleDownload: () => void;
};

export const ImagePreview = ({
  url,
  handleDownload,
}: ImagePreviewProps): JSX.Element => {
  const classes = useStyles();

  return <Image className={classes.image} src={url} onClick={handleDownload} />;
};

type AudioPreviewPureProps = {
  id: string;
  url: string;
  handleDownload: () => void;
};

export const AudioPreviewPure = ({ id, url, handleDownload } : AudioPreviewPureProps) : JSX.Element => {
  const classes = useStyles();
  const [isAudioVisible, setIsAudioVisible] = useState<boolean>(false);

  return (
    <div
      className={classes.audioContainer}
      onMouseOver={() => setIsAudioVisible(true)}
      onMouseLeave={() => setIsAudioVisible(false)}
      onFocus={() => setIsAudioVisible(true)}
      onBlur={() => setIsAudioVisible(false)}
    >
      <DocumentPreviewPure icon={AudioIcon} handleDownload={emptyFunction} />

      <AudioPlayer
        className={clsx([classes.audioControls, isAudioVisible && 'visible'])}
        id={id}
        url={url}
        handleDownload={handleDownload}
      />
    </div>
  );
};

type VideoPreviewPureProps = {
  url: string;
};

export const VideoPreviewPure = ({
  url,
}: VideoPreviewPureProps): JSX.Element => (
  <Video width='150' src={url} labelId='locale' />
);

const getAttachmentPreview = (attachment: S3File): JSX.Element => {
  const fileName = parseFileName(attachment.filename);
  const handleDownload = () => downloadFileFromURL(attachment.s3URL, fileName);

  if (attachment.contentType.includes('audio/')) {
    return (
      <AudioPreviewPure
        id={attachment.id || attachment.key}
        url={attachment.s3URL}
        handleDownload={handleDownload}
      />
    );
  }

  if (attachment.contentType.includes('video/')) {
    return <VideoPreviewPure url={attachment.s3URL} />;
  }

  if (attachment.contentType.includes('image/')) {
    return (
      <ImagePreview url={attachment.s3URL} handleDownload={handleDownload} />
    );
  }

  return (
    <DocumentPreviewPure
      icon={getIconFromContentType(attachment.contentType)}
      handleDownload={handleDownload}
    />
  );
};

const getIconFromContentType = (contentType: string) => {
  if (
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].includes(contentType)
  ) {
    return ExcelIcon;
  } else if (contentType === 'application/pdf') {
    return PdfIcon;
  } else if (
    [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(contentType)
  ) {
    return PowerPointIcon;
  } else if (
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(contentType)
  ) {
    return WordIcon;
  } else {
    return UnknownIcon;
  }
};

const formatFileName = (fileName: string): string => {
  const parts = fileName.split('/');
  return parts[parts.length - 1];
};

type AdminMessageViewerAttachmentsProps = {
  attachments: S3File[];
};

const AdminMessageViewerAttachments = ({
  attachments,
}: AdminMessageViewerAttachmentsProps): JSX.Element | null => {
  const classes = useStyles();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Grid alignItems="center" className={classes.container} container>
      <Grid className={classes.label} item xs={12}>
        <FormattedMessage id="admin.messages.messageView.attachments" />
      </Grid>

      {attachments.map((_) => {
        const formattedFileName = formatFileName(_.filename);

        return (
          <Grid className={classes.item} item key={_.id}>
            {getAttachmentPreview(_)}

            <Tooltip title={formattedFileName}>
              <div className={classes.filename}>{formattedFileName}</div>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default AdminMessageViewerAttachments;
